import { useCallback } from 'react';
import { useCampaignEditorContext } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorContext';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { DateCantBeInThePastRule } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Workflow/Rules/SplitTest/DateCantBeInThePastRule';
import { useWorkflowForm } from '../../../CampaignWorkflowTab/contexts/CampaignWorkflowFormContext';
import { useCampaignSchedulingSelector } from '../../../CampaignWorkflowTab/state/CampaignSettings/selectors/useCampaignSchedulingSelector';

export const startDateFormKey = 'campaign-schedule-start';

const validate = (t, startDate: Date) => {
  if (DateCantBeInThePastRule({ date: startDate }).result === 'fail') {
    return {
      passed: false,
      validationMessage: t('onsite.editor.workflow.scheduling.inlineValidationMessages.startDateInThePast'),
    };
  }

  return {
    passed: true,
    validationMessage: undefined,
  };
};

export const useStartDate = () => {
  const { t } = useTranslation('campaigns');

  const { actions, state } = useWorkflowForm();
  const { dispatch } = useCampaignEditorContext();

  const onFocusChange = useCallback(
    ({ focused }) => {
      actions.setTouched(startDateFormKey, true);
      actions.setFocused(startDateFormKey, focused);
    },
    [actions],
  );

  const { startDate, endDate } = useCampaignSchedulingSelector() ?? {};
  const onDateChange = useCallback(
    (startDate: Moment) => dispatch({ type: 'SET_CAMPAIGN_START_DATE', payload: { startDate: startDate?.toDate() } }),
    [dispatch],
  );

  const validation = validate(t, startDate);

  const minDate = moment();
  const maxDate = endDate ? moment(endDate).add(-1, 'days') : undefined;

  return {
    startDate: startDate ? moment(startDate) : undefined,
    onDateChange,
    onFocusChange,
    focused: state.focused[startDateFormKey],
    validation,
    minDate,
    maxDate,
  };
};
